import { Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { NavigationEnd, Router, RoutesRecognized } from '@angular/router';
import { DomSanitizer, Meta, Title } from '@angular/platform-browser';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { MatSidenav } from '@angular/material/sidenav';
import { LocalStorageService } from 'ngx-webstorage';
import { MenuItem } from '../model/MenuItem';
import { User } from '../model/auth/User';
import { AuthService } from '../service/auth.service';
import { MenuProviderService } from '../service/menu-provider.service';
import { MatIconRegistry } from '@angular/material/icon';
import { UrlHelper } from '../helper/url.helper';
import { NotificationHandler } from '../helper/notification.handler';
import { Session } from '../model/auth/Session';
import { TenantService } from '../service/tenant.service';
import { Competition } from '../model/competition/Competition';
import { TrackingService } from '../service/tracking.service';
import { PaymentStatus } from '../model/Payment';
import { ContestService } from '../service/contest.service';
import { Contest } from '../model/contest/Contest';
import { Role } from '../model/auth/Role';
import { map } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import smartlookClient from 'smartlook-client';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {

  private DEFAULT_TITLE = 'QuizWin';

  private routeEventsSubscription: Subscription | undefined;
  private userChangeSubscription: Subscription | undefined;
  private enrolRequestChangeSubscription: Subscription | undefined;
  private breakpointChangeSubscription: Subscription | undefined;
  private tenantSubscription: Subscription | undefined;

  mailMenu: MenuItem[] = [];
  supportMenu: MenuItem[] = [];

  user!: User | null;
  session!: Session | null;
  tenant!: Competition | null;

  dataLoaded = false;

  mobileView = false;
  readonly inIframe: boolean;
  examView = false;

  pendingRequestsChecked = false;
  pendingTeacherRequests = 0;

  constructor(private authService: AuthService,
              private router: Router,
              private titleService: Title,
              private metaService: Meta,
              private trackingService: TrackingService,
              private tenantService: TenantService,
              private contestService: ContestService,
              private menuProviderService: MenuProviderService,
              private localStorageService: LocalStorageService,
              private breakpointObserver: BreakpointObserver,
              matIconRegistry: MatIconRegistry,
              domSanitizer: DomSanitizer) {
    this.trackingService.configureAnalytics();

    this.titleService.setTitle(this.DEFAULT_TITLE);

    AppComponent.registerIcon(matIconRegistry, domSanitizer, 'contests-icon', 'assets/menu/sidenav-contests-icon.svg');
    AppComponent.registerIcon(matIconRegistry, domSanitizer, 'competitions-icon', 'assets/menu/sidenav-competitions-icon.svg');
    AppComponent.registerIcon(matIconRegistry, domSanitizer, 'teachers-icon', 'assets/menu/sidenav-teachers-icon.svg');
    AppComponent.registerIcon(matIconRegistry, domSanitizer, 'students-icon', 'assets/menu/sidenav-students-icon.svg');
    AppComponent.registerIcon(matIconRegistry, domSanitizer, 'hosts-icon', 'assets/menu/sidenav-hosts-icon.svg');
    AppComponent.registerIcon(matIconRegistry, domSanitizer, 'migrations-icon', 'assets/menu/sidenav-migrations-icon.svg');
    AppComponent.registerIcon(matIconRegistry, domSanitizer, 'settings-icon', 'assets/menu/sidenav-settings-icon.svg');
    AppComponent.registerIcon(matIconRegistry, domSanitizer, 'stats-icon', 'assets/menu/sidenav-stats-icon.svg');
    AppComponent.registerIcon(matIconRegistry, domSanitizer, 'coordinators-icon', 'assets/menu/sidenav-hosts-icon.svg');
    AppComponent.registerIcon(matIconRegistry, domSanitizer, 'help-icon', 'assets/menu/sidenav-help-icon.svg');
    AppComponent.registerIcon(matIconRegistry, domSanitizer, 'sign-out-icon', 'assets/menu/sidenav-sign-out-icon.svg');

    this.inIframe = AppComponent.openedInIframe;
  }

  private static registerIcon(matIconRegistry: MatIconRegistry, domSanitizer: DomSanitizer,
                              name: string, fileName: string) {
    matIconRegistry.addSvgIcon(
      name,
      domSanitizer.bypassSecurityTrustResourceUrl(fileName)
    );
  }

  private static get openedInIframe() {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  ngOnInit(): void {
    this.tenantService.loadTenantDetails();
    this.tenant = this.tenantService.getTenant();
    AppComponent.setSentryTenant(this.tenant);
    Sentry.setTag('service', 'ui');

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.routeEventsSubscription = this.router.events.subscribe(async event => {
      if (event instanceof NavigationEnd) {
        this.trackingService.trackPageView(event);

        await this.getUser();
      } else if (event instanceof RoutesRecognized) {
        const firstChild = event.state.root.firstChild;
        const routeData = firstChild ? firstChild.data : {};
        const innerChildData = firstChild?.firstChild ? firstChild.firstChild.data : null;

        let title = this.tenant && this.tenant.name
          ? this.tenant.name + ' | ' + this.DEFAULT_TITLE
          : this.DEFAULT_TITLE;
        const innerChildTitle = innerChildData ? ` | ${innerChildData.title as string}` : '';
        if (routeData.title) {
          title = `${routeData.title as string}${innerChildTitle} | ${title}`;
        }

        this.titleService.setTitle(title);
        this.metaService.updateTag({property: 'og:title', content: title}, 'property=\'og:title\'');
        this.metaService.updateTag({name: 'twitter:title', content: title}, 'name=\'twitter:title\'');
      }
    });

    this.breakpointChangeSubscription = this.breakpointObserver
      .observe([Breakpoints.HandsetPortrait])
      .subscribe((state: BreakpointState) => {
        this.mobileView = state.matches;
      });

    this.tenantSubscription = this.tenantService.getTenantUpdates().subscribe(tenant => {
      this.tenant = tenant;

      AppComponent.setSentryTenant(tenant);
    });

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.userChangeSubscription = this.authService.userChanged.subscribe(async () => await this.getUser());
  }

  private static setSentryTenant(tenant: Competition | null) {
    if (tenant) {
      Sentry.setContext('tenant', {
        id: tenant.id,
        name: tenant.name,
        domain: tenant.domain,
      });
      Sentry.setTag('tenant_id', tenant.id);
      Sentry.setTag('tenant_domain', tenant.domain);
    }
  }

  ngOnDestroy(): void {
    if (this.routeEventsSubscription) this.routeEventsSubscription.unsubscribe();
    if (this.userChangeSubscription) this.userChangeSubscription.unsubscribe();
    if (this.enrolRequestChangeSubscription) this.enrolRequestChangeSubscription.unsubscribe();
    if (this.breakpointChangeSubscription) this.breakpointChangeSubscription.unsubscribe();
    this.tenantService.unsubscribe(this.tenantSubscription);
  }

  get profileInfoUrl(): string {
    return UrlHelper.PROFILE_DETAILS_PATH;
  }

  private async getUser(): Promise<void> {
    this.user = await this.authService.getUser();
    this.session = await this.authService.getSession();

    if (this.user) {
      this.trackingService.trackUser(this.user);

      Sentry.setUser({
        id: this.user.id,
        email: this.user.email,
        username: this.user.username,
        first_name: this.user.firstName,
        last_name: this.user.lastName,
        role: this.session ? this.session.role : undefined
      });

      if (environment.smartlook.enabled) {
        smartlookClient.identify(this.user.id, {
          email: this.user.email,
          username: this.user.username || '',
          first_name: this.user.firstName,
          last_name: this.user.lastName,
          role: this.session?.role || 'unknown',
          tenant_id: this.tenant?.id || 'unknown',
          tenant_domain: this.tenant?.domain || 'unknown',
        });
      }

      if (this.session && !this.session.role) {
        const url = this.router.url;
        if (!url.startsWith(UrlHelper.ROLE_SELECT_PATH) && !url.startsWith(UrlHelper.SIGN_UP_CONFIRM_PATH)) {
          await this.router.navigate([UrlHelper.ROLE_SELECT_PATH]);
        } else {
          return;
        }
      } else if (this.session && (this.session.isTeacher() || this.session.isStudent()) && !this.user.schoolId) {
        await this.router.navigate([UrlHelper.SCHOOL_SELECT_PATH], {skipLocationChange: true});
      } else {
        this.examView = this.router.url.startsWith(UrlHelper.CONTEST_EXAM_PATH)
          || this.router.url.startsWith(UrlHelper.STATS_SCHOOL_RATING_PATH)
          || this.router.url.startsWith(UrlHelper.STATS_REGION_SCHOOLS_PATH);

        this.mailMenu = await this.menuProviderService.mainMenu();
        this.supportMenu = this.menuProviderService.supportMenu();

        if (!this.pendingRequestsChecked) this.loadPendingApprovals();

        if (this.session && (this.session.isHost() || this.session.isCoordinator())) {
          this.enrolRequestChangeSubscription = this.contestService.enrolRequestsChanged
            .subscribe(() => this.loadPendingApprovals());
        }
      }
    } else {
      this.mailMenu = [];
      this.supportMenu = [];
    }

    this.dataLoaded = true;
  }

  async handleMenuItemSelection(sidenav: MatSidenav) {
    await this.closeSidenav(sidenav);
  }

  private async closeSidenav(sidenav: MatSidenav) {
    if (this.mobileView) {
      await sidenav.close();
    }
  }

  async signOut(sidenav: MatSidenav) {
    try {
      await this.authService.logout();

      this.user = null;
      await this.closeSidenav(sidenav);
      this.localStorageService.clear();
      this.pendingRequestsChecked = false;
      Sentry.configureScope(scope => scope.setUser(null));

      if (environment.smartlook.enabled) smartlookClient.anonymize();
    } catch (e) {
      NotificationHandler.handleError(e);
    } finally {
      await this.router.navigate([UrlHelper.SIGN_IN_PATH], {replaceUrl: true});
    }
  }

  private loadPendingApprovals() {
    if (!this.tenant || !this.tenant.id) return;
    if (!this.session || (!this.session.isHost() && !this.session.isCoordinator())) return;

    const region = this.session.isCoordinator() ? this.user?.region : '-1';

    if (region) {
      this.pendingRequestsChecked = true;
      this.contestService.getContests(this.tenant.id).subscribe(contests => {
        const today = new Date();
        const futureContests = contests
          .filter(contest => contest.paymentStatus == PaymentStatus.successful || this.session?.isHost())
          .filter(contest => contest.endsAt && contest.endsAt > today);

        if (this.tenant && this.tenant.id && region && futureContests.length > 0) {
          const futures: Observable<User[]>[] = [];
          futureContests.forEach(contest =>
            futures.push(this.loadPendingApprovalForContest(this.tenant?.id || '', contest, region)));

          forkJoin(futures).subscribe((users: User[][]) => {
            this.pendingTeacherRequests = users.map(u => u.length).reduce((l1, l2) => l1 + l2);
          }, error => {
            NotificationHandler.handleError($localize`:Notifications|Pending requests fetch has failed message@@notifications-pending-requests-fetch-failed-error:Pending requests fetch has failed`, error);
          });
        }
      }, error => NotificationHandler.handleError($localize`:Notifications|Contests fetch has failed message@@notifications-contests-fetch-failed-error:Contests fetch has failed`, error));
    }
  }

  private loadPendingApprovalForContest(competitionId: string, contest: Contest, region: string): Observable<User[]> {
    return this.contestService.getPayments(competitionId, contest.id, region, Role.Teacher, PaymentStatus.review)
      .pipe(
        map(page => page.content || [])
      );
  }
}
